import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Clock, CheckCircle, XCircle, AlertTriangle, ChevronLeft, ChevronRight, Shield } from 'lucide-react';

// ─── Mobile detection ────────────────────────────────────────────────────────
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 0 && window.innerWidth < 1024);
};

// ─── Option labels ────────────────────────────────────────────────────────────
const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E'];

// ─── Render passage text with **bold** support ────────────────────────────────
const renderPassageText = (text) => {
  const parts = text.split(/\*\*(.*?)\*\*/gs);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
};

const AptitudeTest = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  // Phase: briefing → active → submitted
  const [phase, setPhase] = useState('loading'); // loading | mobile_blocked | briefing | active | submitted | error
  const [test, setTest] = useState(null);
  const [testError, setTestError] = useState(null);

  // Test state
  const [currentPassageIndex, setCurrentPassageIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // {question_id: "A"|"B"|"C"|"D"|"E"}
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitConfirmUnanswered, setSubmitConfirmUnanswered] = useState(0);

  // Proctoring state
  const [violations, setViolations] = useState([]);
  const [showViolationOverlay, setShowViolationOverlay] = useState(false);
  const [violationMessage, setViolationMessage] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Per-passage timing
  const passageStartTimeRef = useRef(null);
  const passageTimingsRef = useRef({});
  // Violations ref — always current even inside stale closures (logViolation has [] deps)
  const violationsRef = useRef([]);
  // Grace period: suppress blur violations for 3s after test starts (fullscreen transition noise)
  const testStartTimeRef = useRef(null);
  // Always-current ref to handleSubmit so stale closures (logViolation) call the latest version
  const handleSubmitRef = useRef(null);

  // Draft save interval ref
  const draftSaveIntervalRef = useRef(null);

  // ─── Load test ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isMobile()) {
      setPhase('mobile_blocked');
      return;
    }
    fetchTest();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchTest = async () => {
    try {
      const response = await axios.get(`${API}/aptitude-tests/token/${token}`);
      const testData = response.data;
      setTest(testData);

      if (testData.completed || testData.token_status === 'used') {
        setPhase('submitted');
        return;
      }

      // Restore draft answers from server first, then localStorage as fallback
      const serverDraft = testData.draft_answers;
      const localDraft = (() => {
        try { return JSON.parse(localStorage.getItem(`test_draft_${token}`) || 'null'); } catch { return null; }
      })();
      if (serverDraft && Object.keys(serverDraft).length > 0) {
        setAnswers(serverDraft);
      } else if (localDraft) {
        setAnswers(localDraft);
      }

      setPhase('briefing');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 423) {
        setTestError({ type: 'session_ended', message: detail || 'Your assessment session has ended.' });
      } else if (status === 410) {
        setTestError({ type: 'expired', message: detail || 'This test link has expired.' });
      } else if (status === 404) {
        setTestError({ type: 'not_found', message: detail || 'Test link not found. Please check your email.' });
      } else {
        setTestError({ type: 'error', message: 'Failed to load test. Please refresh the page.' });
      }
      setPhase('error');
    }
  };

  // ─── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'active' || !test) return;
    if (timeRemaining === null) {
      setTimeRemaining((test.time_limit_minutes || 60) * 60);
      return;
    }
    if (timeRemaining === 0) {
      handleSubmit(true); // auto-submit on time
      return;
    }
    const timer = setInterval(() => {
      setTimeRemaining(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, timeRemaining, test]);

  // ─── Proctoring setup (only when active) ────────────────────────────────────
  useEffect(() => {
    if (phase !== 'active') return;

    // Block all keyboard input and clipboard events
    const blockEvent = (e) => e.preventDefault();
    const blockKeyboard = (e) => {
      if (e.key === 'PrintScreen') {
        logViolation('screenshot_attempt');
      }
      e.preventDefault();
    };

    document.addEventListener('copy', blockEvent);
    document.addEventListener('cut', blockEvent);
    document.addEventListener('paste', blockEvent);
    document.addEventListener('contextmenu', blockEvent);
    document.addEventListener('keydown', blockKeyboard);

    // Tab switch / blur detection
    const handleVisibilityChange = () => {
      if (document.hidden) logViolation('tab_switch');
    };
    const handleBlur = () => {
      // Suppress blur for 3s after start — fullscreen transition fires spurious blur events
      if (testStartTimeRef.current && Date.now() - testStartTimeRef.current < 3000) return;
      // Don't double-count when violation overlay is already showing
      if (document.querySelector('[data-violation-overlay]')) return;
      logViolation('window_blur');
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);

    // Fullscreen change
    const handleFullscreenChange = () => {
      const inFullscreen = !!document.fullscreenElement;
      setIsFullscreen(inFullscreen);
      if (!inFullscreen) logViolation('fullscreen_exit');
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('copy', blockEvent);
      document.removeEventListener('cut', blockEvent);
      document.removeEventListener('paste', blockEvent);
      document.removeEventListener('contextmenu', blockEvent);
      document.removeEventListener('keydown', blockKeyboard);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ─── Periodic draft save ────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'active') return;
    draftSaveIntervalRef.current = setInterval(() => {
      saveDraftToServer();
    }, 60000); // every 60 seconds
    return () => clearInterval(draftSaveIntervalRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, answers, violations]);

  const saveDraftToServer = useCallback(async () => {
    if (!test || !token) return;
    try {
      await axios.put(`${API}/aptitude-tests/token/${token}/save-progress`, {
        draft_answers: answers,
        violations: violationsRef.current,
        passage_timings: passageTimingsRef.current,
      });
    } catch {
      // Silent fail — localStorage is backup
    }
  }, [test, token, answers, violations]);

  // ─── Log violation ──────────────────────────────────────────────────────────
  const logViolation = useCallback((type) => {
    const newViolation = { type, timestamp: new Date().toISOString() };
    setViolations(prev => {
      const updated = [...prev, newViolation];
      violationsRef.current = updated; // keep ref in sync so handleSubmit always sees latest
      const severeCount = updated.filter(v => ['tab_switch', 'fullscreen_exit', 'window_blur', 'screenshot_attempt'].includes(v.type)).length;
      if (severeCount >= 5) {
        setTimeout(() => handleSubmitRef.current?.(true), 500);
        return updated;
      }
      if (['tab_switch', 'fullscreen_exit', 'screenshot_attempt'].includes(type)) {
        const remaining = 5 - severeCount;
        setViolationMessage(
          type === 'tab_switch'
            ? `You left the test window. This has been recorded. (${remaining} warning${remaining !== 1 ? 's' : ''} remaining before auto-submission)`
            : type === 'screenshot_attempt'
            ? `Screenshot attempt detected and blocked. This has been recorded. (${remaining} warning${remaining !== 1 ? 's' : ''} remaining before auto-submission)`
            : `You exited fullscreen. Please return to fullscreen to continue. (${remaining} warning${remaining !== 1 ? 's' : ''} remaining)`
        );
        setShowViolationOverlay(true);
      }
      return updated;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Start test (enter fullscreen) ──────────────────────────────────────────
  const handleStartTest = async () => {
    try {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } catch {
      toast.error('Fullscreen is required to begin the assessment. Please allow fullscreen when prompted and try again.');
      return;
    }
    try {
      await axios.post(`/api/aptitude-tests/token/${token}/start`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error('This assessment has already been submitted.');
        return;
      }
      if (status === 410) {
        toast.error(err?.response?.data?.detail || 'This test link is no longer valid.');
        return;
      }
      // 200 or "already active" — allow through
    }
    passageStartTimeRef.current = Date.now();
    testStartTimeRef.current = Date.now();
    setPhase('active');
  };

  // ─── Passage navigation ─────────────────────────────────────────────────────
  const recordPassageTiming = (index) => {
    if (passageStartTimeRef.current && test?.passages?.[index]) {
      const elapsed = Math.round((Date.now() - passageStartTimeRef.current) / 1000);
      const pid = test.passages[index].passage_id;
      passageTimingsRef.current[pid] = (passageTimingsRef.current[pid] || 0) + elapsed;
    }
    passageStartTimeRef.current = Date.now();
  };

  const goToPassage = (index) => {
    recordPassageTiming(currentPassageIndex);
    setCurrentPassageIndex(index);
    window.scrollTo(0, 0);
  };

  // ─── Answer change ──────────────────────────────────────────────────────────
  const handleAnswerChange = (questionId, value) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    // Save to localStorage immediately
    try { localStorage.setItem(`test_draft_${token}`, JSON.stringify(newAnswers)); } catch {}
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (autoSubmit = false) => {
    if (submitting) return;

    if (!autoSubmit) {
      const answered = Object.keys(answers).length;
      const total = test?.passages?.reduce((s, p) => s + p.questions.length, 0) || 0;
      if (answered < total) {
        setSubmitConfirmUnanswered(total - answered);
        setShowSubmitConfirm(true);
        return;
      }
    }

    setSubmitting(true);
    setSubmitError(null);

    // Record final passage timing
    recordPassageTiming(currentPassageIndex);

    // Clear draft save interval
    clearInterval(draftSaveIntervalRef.current);

    const payload = {
      answers,
      violations: violationsRef.current,
      passage_timings: passageTimingsRef.current,
    };

    // Retry up to 3 times
    let lastError = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await axios.post(`${API}/aptitude-tests/token/${token}/submit`, payload);
        // Clear localStorage on success
        try { localStorage.removeItem(`test_draft_${token}`); } catch {}
        // Exit fullscreen
        if (document.fullscreenElement) {
          try { await document.exitFullscreen(); } catch {}
        }
        setPhase('submitted');
        setSubmitting(false);
        return;
      } catch (err) {
        lastError = err;
        if (err.response?.status === 409 || err.response?.data?.already_submitted) {
          // Already submitted — idempotent success
          setPhase('submitted');
          setSubmitting(false);
          return;
        }
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 1500));
        }
      }
    }

    // All retries failed
    setSubmitError('Submission failed. Your answers are saved locally. Please try again or contact admissions.');
    setSubmitting(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting, answers, violations, token, currentPassageIndex, test]);

  // Keep ref current so logViolation (empty deps) always calls the latest handleSubmit
  useEffect(() => { handleSubmitRef.current = handleSubmit; }, [handleSubmit]);

  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPassageProgress = (passageIndex) => {
    if (!test?.passages?.[passageIndex]) return { answered: 0, total: 0 };
    const questions = test.passages[passageIndex].questions;
    const answered = questions.filter(q => answers[q.id]).length;
    return { answered, total: questions.length };
  };

  const totalAnswered = () => {
    if (!test?.passages) return 0;
    return test.passages.reduce((sum, p) => sum + p.questions.filter(q => answers[q.id]).length, 0);
  };

  const totalQuestions = () => {
    if (!test?.passages) return 0;
    return test.passages.reduce((sum, p) => sum + p.questions.length, 0);
  };

  // ─── Render states ──────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-playfair">Loading test...</div>
      </div>
    );
  }

  if (phase === 'mobile_blocked') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted p-4">
        <Card className="max-w-md p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-primary mb-2">Desktop Required</h2>
          <p className="text-gray-600 mb-4">This assessment must be completed on a desktop or laptop computer. Mobile devices are not permitted.</p>
          <p className="text-sm text-muted-foreground">Please access your test link from a desktop browser.</p>
        </Card>
      </div>
    );
  }

  if (phase === 'error') {
    const isSessionEnded = testError?.type === 'session_ended';
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted p-4">
        <Card className="max-w-md p-8 text-center">
          {isSessionEnded
            ? <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            : testError?.type === 'expired'
            ? <Clock className="w-16 h-16 text-red-500 mx-auto mb-4" />
            : <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />}
          <h2 className="text-2xl font-bold text-primary mb-2">
            {isSessionEnded ? 'Session Ended'
              : testError?.type === 'expired' ? 'Test Link Expired'
              : testError?.type === 'not_found' ? 'Link Not Found'
              : 'Test Unavailable'}
          </h2>
          <p className="text-gray-600 mb-4">{isSessionEnded
            ? 'The assessment must be completed in one sitting. Your session ended when you closed or left the page.'
            : testError?.message}
          </p>
          {isSessionEnded && (
            <p className="text-sm text-gray-500 mb-4">If you experienced a technical issue, please contact admissions and request a new link.</p>
          )}
          <p className="text-sm text-muted-foreground">Contact <a href="mailto:academy@fbattorneys.co.tz" className="text-secondary underline">academy@fbattorneys.co.tz</a> if you need assistance.</p>
        </Card>
      </div>
    );
  }

  if (phase === 'submitted') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted p-4">
        <Card className="max-w-md p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-primary mb-2">Assessment Submitted</h2>
          <p className="text-gray-600 mb-2">Thank you for completing the Academic Assessment.</p>
          <p className="text-sm text-muted-foreground mb-6">Your results are under review. You will be contacted by the admissions team regarding the outcome.</p>
          <Button onClick={() => navigate('/')} className="btn-primary">Return to Home</Button>
        </Card>
      </div>
    );
  }

  // ─── Briefing screen ─────────────────────────────────────────────────────────
  if (phase === 'briefing') {
    const totalQ = totalQuestions();
    const passageCount = test?.passages?.length || 0;
    return (
      <div className="min-h-screen bg-muted py-12 px-4">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-playfair font-bold text-primary">Academic Assessment</h1>
          </div>

          {/* Test overview */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-primary mb-4">Assessment Overview</h2>
            <div className="grid grid-cols-3 gap-4 text-center mb-4">
              <div className="bg-muted rounded-lg p-3">
                <div className="text-2xl font-bold text-primary">{passageCount}</div>
                <div className="text-xs text-muted-foreground">Passages</div>
              </div>
              <div className="bg-muted rounded-lg p-3">
                <div className="text-2xl font-bold text-primary">{totalQ}</div>
                <div className="text-xs text-muted-foreground">Questions</div>
              </div>
              <div className="bg-muted rounded-lg p-3">
                <div className="text-2xl font-bold text-primary">{test?.time_limit_minutes || 60}</div>
                <div className="text-xs text-muted-foreground">Minutes</div>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              You will read {passageCount} passages and answer {totalQ} multiple-choice questions. All questions are based solely on the passage text provided — no prior knowledge is required.
            </p>
          </Card>

          {/* Monitoring disclosure */}
          <Card className="p-6 border-amber-200 bg-amber-50">
            <h2 className="text-lg font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Proctoring &amp; Monitoring
            </h2>
            <ul className="text-sm text-amber-800 space-y-1">
              <li>• This assessment is monitored. The following will be recorded:</li>
              <li className="ml-4">— Tab switches and window focus changes</li>
              <li className="ml-4">— Fullscreen exits</li>
              <li className="ml-4">— Copy/paste attempts</li>
              <li>• Copy, paste, and right-click are disabled during the test</li>
              <li>• The test will auto-submit if you leave the window 5 or more times</li>
              <li>• The assessment must be completed in one sitting — closing or leaving this page will end your session permanently</li>
            </ul>
          </Card>

          {/* Rules */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-primary mb-3">Instructions</h2>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>1. Read each passage carefully before answering the questions below it</li>
              <li>2. Select the option that is most strongly supported by the text</li>
              <li>3. You may navigate freely between passages at any time</li>
              <li>4. All questions must be answered before submitting</li>
              <li>5. The timer counts down from {test?.time_limit_minutes || 60} minutes — the test auto-submits when time expires</li>
              <li>6. Do not close this browser tab once the test has started</li>
            </ul>
          </Card>

          <Button onClick={handleStartTest} className="btn-primary w-full py-6 text-lg">
            Enter Fullscreen &amp; Begin Assessment
          </Button>
          <p className="text-xs text-center text-muted-foreground">By starting the test you confirm you have read and understood the above instructions.</p>
        </div>
      </div>
    );
  }

  // ─── Active test ─────────────────────────────────────────────────────────────
  const currentPassage = test?.passages?.[currentPassageIndex];
  const timeColor = timeRemaining !== null && timeRemaining < 300 ? 'text-red-300' : 'text-white';

  return (
    <div className="min-h-screen bg-muted" style={{ userSelect: 'none' }}>
      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <Card className="max-w-md p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-primary mb-2">Unanswered Questions</h2>
            <p className="text-gray-600 mb-2 text-sm">
              You have <strong>{submitConfirmUnanswered}</strong> unanswered question{submitConfirmUnanswered !== 1 ? 's' : ''}.
            </p>
            <p className="text-gray-500 mb-6 text-sm">Once submitted, you cannot change your answers.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowSubmitConfirm(false)}>
                Go Back
              </Button>
              <Button
                className="btn-secondary flex-1"
                onClick={() => { setShowSubmitConfirm(false); handleSubmitRef.current?.(true); }}
              >
                Submit Anyway
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Violation Overlay */}
      {showViolationOverlay && (
        <div data-violation-overlay className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="max-w-md p-8 text-center border-red-200">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-primary mb-2">Warning</h2>
            <p className="text-gray-600 mb-6 text-sm">{violationMessage}</p>
            <Button
              onClick={() => {
                setShowViolationOverlay(false);
                if (!document.fullscreenElement) {
                  document.documentElement.requestFullscreen().catch(() => {});
                }
              }}
              className="btn-primary w-full"
            >
              I Understand — Return to Test
            </Button>
          </Card>
        </div>
      )}

      {/* Top bar */}
      <div className="bg-primary text-white px-6 py-4 flex items-center justify-between sticky top-0 z-40">
        <div>
          <h1 className="font-playfair font-bold text-lg">Academic Assessment</h1>
          <p className="text-xs text-gray-300">{totalAnswered()} / {totalQuestions()} answered</p>
        </div>

        {/* Passage dots */}
        <div className="flex gap-2">
          {test.passages.map((p, i) => {
            const { answered, total } = getPassageProgress(i);
            const allDone = answered === total;
            const partial = answered > 0 && !allDone;
            return (
              <button
                key={i}
                onClick={() => goToPassage(i)}
                title={`Passage ${i + 1}: ${answered}/${total} answered`}
                className={`w-7 h-7 rounded-full text-xs font-bold border-2 transition-all ${
                  i === currentPassageIndex
                    ? 'bg-secondary border-secondary text-white scale-110'
                    : allDone
                    ? 'bg-green-500 border-green-500 text-white'
                    : partial
                    ? 'bg-amber-400 border-amber-400 text-white'
                    : 'bg-transparent border-white/50 text-white/70 hover:border-white'
                }`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>

        <div className="text-right">
          <div className={`flex items-center gap-2 text-xl font-bold ${timeColor}`}>
            <Clock className="w-5 h-5" />
            <span>{formatTime(timeRemaining)}</span>
          </div>
          <p className="text-xs text-gray-300">Time Remaining</p>
        </div>
      </div>

      {/* Passage content */}
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {currentPassage && (
          <>
            {/* Passage header */}
            <div className="bg-white rounded-sm shadow-sm p-6 border-l-4 border-secondary">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-secondary uppercase tracking-wider">
                  Passage {currentPassageIndex + 1} of {test.passages.length}
                </span>
                {currentPassage.source && (
                  <span className="text-xs text-muted-foreground italic">{currentPassage.source}</span>
                )}
              </div>
              <h2 className="text-xl font-playfair font-bold text-primary mb-4">{currentPassage.title}</h2>
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-[0.95rem]">
                {renderPassageText(currentPassage.text)}
              </div>
            </div>

            {/* Questions for this passage */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider">
                Multiple Choice Questions — Passage {currentPassageIndex + 1}
              </h3>
              {currentPassage.questions.map((question, qIndex) => (
                <Card key={question.id} className={`p-6 ${answers[question.id] ? 'border-green-200 bg-green-50/30' : ''}`}>
                  <p className="font-medium text-primary mb-4">
                    <span className="text-secondary font-bold mr-2">Q{qIndex + 1}.</span>
                    {question.question}
                  </p>
                  <RadioGroup
                    value={answers[question.id] || ''}
                    onValueChange={(val) => handleAnswerChange(question.id, val)}
                  >
                    <div className="space-y-2">
                      {question.options.map((option, optIndex) => {
                        const label = OPTION_LABELS[optIndex] || String(optIndex);
                        return (
                          <div
                            key={optIndex}
                            className={`flex items-start gap-3 p-3 rounded-md cursor-pointer transition-colors ${
                              answers[question.id] === label
                                ? 'bg-secondary/10 border border-secondary/30'
                                : 'hover:bg-muted border border-transparent'
                            }`}
                            onClick={() => handleAnswerChange(question.id, label)}
                          >
                            <RadioGroupItem value={label} id={`${question.id}-${label}`} className="mt-0.5" />
                            <Label htmlFor={`${question.id}-${label}`} className="cursor-pointer flex-1 text-sm">
                              <span className="font-semibold text-secondary mr-2">{label}.</span>
                              {option}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </RadioGroup>
                </Card>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                onClick={() => goToPassage(currentPassageIndex - 1)}
                disabled={currentPassageIndex === 0}
                className="gap-2"
              >
                <ChevronLeft className="w-4 h-4" /> Previous Passage
              </Button>

              {currentPassageIndex < test.passages.length - 1 ? (
                <Button onClick={() => goToPassage(currentPassageIndex + 1)} className="btn-primary gap-2">
                  Next Passage <ChevronRight className="w-4 h-4" />
                </Button>
              ) : (
                <div className="text-right">
                  {submitError && (
                    <p className="text-red-600 text-sm mb-2">{submitError}</p>
                  )}
                  <Button
                    onClick={() => handleSubmit(false)}
                    disabled={submitting}
                    className="btn-secondary px-8 py-3 text-base"
                  >
                    {submitting ? 'Submitting...' : `Submit Assessment (${totalAnswered()}/${totalQuestions()} answered)`}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AptitudeTest;
